/**
 * Centralized logging utility
 * Replaces console.log statements throughout the app
 * Respects environment variables and log levels
 *
 * Features:
 * - Structured logging with context
 * - Performance monitoring
 * - API call tracking
 * - Development-friendly formatting
 * - Production-ready error reporting
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  enabled: boolean
  level: LogLevel
  isDevelopment: boolean
}

interface LogContext {
  module?: string
  userId?: string
  requestId?: string
  [key: string]: any
}

const config: LoggerConfig = {
  enabled: true,
  level: (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info',
  isDevelopment: import.meta.env.DEV
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
}

// Color codes for development console
const LOG_COLORS: Record<LogLevel, string> = {
  debug: '#9CA3AF',  // Gray
  info: '#3B82F6',   // Blue
  warn: '#F59E0B',   // Orange
  error: '#EF4444'   // Red
}

class Logger {
  private shouldLog(level: LogLevel): boolean {
    if (!config.enabled) return false
    return LOG_LEVELS[level] >= LOG_LEVELS[config.level]
  }

  private formatMessage(level: LogLevel, message: string, data?: any): void {
    if (!this.shouldLog(level)) return

    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`

    switch (level) {
      case 'debug':
        if (config.isDevelopment) {
          console.log(prefix, message, data || '')
        }
        break
      case 'info':
        console.info(prefix, message, data || '')
        break
      case 'warn':
        console.warn(prefix, message, data || '')
        break
      case 'error':
        console.error(prefix, message, data || '')
        // In production, send to error tracking service (Sentry, etc.)
        if (!config.isDevelopment && typeof window !== 'undefined') {
          // TODO: Send to error tracking service
          // Sentry.captureException(data)
        }
        break
    }
  }

  debug(message: string, data?: any): void {
    this.formatMessage('debug', message, data)
  }

  info(message: string, data?: any): void {
    this.formatMessage('info', message, data)
  }

  warn(message: string, data?: any): void {
    this.formatMessage('warn', message, data)
  }

  error(message: string, data?: any): void {
    this.formatMessage('error', message, data)
  }

  /**
   * Group related logs (development only)
   */
  group(label: string, callback: () => void): void {
    if (!config.isDevelopment) {
      callback()
      return
    }
    console.group(label)
    callback()
    console.groupEnd()
  }

  /**
   * Time an operation (development only)
   */
  time(label: string): void {
    if (config.isDevelopment) {
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (config.isDevelopment) {
      console.timeEnd(label)
    }
  }

  /**
   * Create a namespaced logger for a specific module
   * Example: const log = logger.module('QuoteInbox')
   */
  module(moduleName: string) {
    return {
      debug: (message: string, data?: any) => this.debug(`[${moduleName}] ${message}`, data),
      info: (message: string, data?: any) => this.info(`[${moduleName}] ${message}`, data),
      warn: (message: string, data?: any) => this.warn(`[${moduleName}] ${message}`, data),
      error: (message: string, data?: any) => this.error(`[${moduleName}] ${message}`, data),
    }
  }

  /**
   * Log API calls with structured format
   * Example: logger.api('POST', '/api/quotes', { status: 200, duration: 145 })
   */
  api(method: string, endpoint: string, data?: { status?: number; duration?: number; error?: any }): void {
    const { status, duration, error } = data || {}
    const emoji = status && status >= 200 && status < 300 ? '✓' : '✗'
    const level = error ? 'error' : 'info'

    const message = `${emoji} ${method} ${endpoint}${status ? ` [${status}]` : ''}${duration ? ` (${duration}ms)` : ''}`

    if (config.isDevelopment && !error) {
      // Pretty format for dev console
      const color = status && status >= 200 && status < 300 ? '#10B981' : '#EF4444'
      console.log(
        `%c${message}`,
        `color: ${color}; font-weight: bold;`,
        data || ''
      )
    } else {
      this.formatMessage(level as LogLevel, message, data)
    }
  }

  /**
   * Log AI/Tool calls with structured format
   * Example: logger.aiCall('elevenlabs', 'get_lead_context', { success: true })
   */
  aiCall(service: string, action: string, data?: any): void {
    const emoji = data?.success !== false ? '🤖' : '⚠️'
    const message = `${emoji} [${service.toUpperCase()}] ${action}`

    if (config.isDevelopment) {
      console.log(
        `%c${message}`,
        `color: #8B5CF6; font-weight: bold;`,
        data || ''
      )
    } else {
      this.info(message, data)
    }
  }

  /**
   * Performance measurement helper
   * Example:
   * const perf = logger.perf('Database Query')
   * // ... do work ...
   * perf.end() // Logs duration
   */
  perf(label: string) {
    const startTime = performance.now()
    return {
      end: () => {
        const duration = Math.round(performance.now() - startTime)
        if (config.isDevelopment) {
          console.log(
            `%c⏱️ ${label}`,
            'color: #06B6D4; font-weight: bold;',
            `${duration}ms`
          )
        } else {
          this.debug(`${label} completed in ${duration}ms`)
        }
      }
    }
  }

  /**
   * Log with custom context (userId, requestId, etc.)
   * Example: logger.withContext({ userId: '123' }).info('User action', data)
   */
  withContext(context: LogContext) {
    return {
      debug: (message: string, data?: any) => {
        this.debug(message, { ...context, ...data })
      },
      info: (message: string, data?: any) => {
        this.info(message, { ...context, ...data })
      },
      warn: (message: string, data?: any) => {
        this.warn(message, { ...context, ...data })
      },
      error: (message: string, data?: any) => {
        this.error(message, { ...context, ...data })
      },
    }
  }

  /**
   * Pretty print objects in development
   */
  table(data: any[] | Record<string, any>): void {
    if (config.isDevelopment && this.shouldLog('debug')) {
      console.table(data)
    }
  }
}

export const logger = new Logger()

// Export convenience functions
export const { debug, info, warn, error, group, time, timeEnd, module, api, aiCall, perf, withContext, table } = logger

// =============================================================================
// PII MASKING UTILITIES
// =============================================================================

/**
 * Mask an email address for logging while preserving debuggability
 * @param email - Full email address
 * @returns Masked email like "ja***@gmail.com"
 * @example maskEmail("jason@gmail.com") → "ja***@gmail.com"
 * @example maskEmail("a@b.com") → "a***@b.com"
 */
export function maskEmail(email: string | undefined | null): string {
  if (!email || typeof email !== 'string') return '[no-email]'
  
  const atIndex = email.indexOf('@')
  if (atIndex < 1) return '[invalid-email]'
  
  const localPart = email.substring(0, atIndex)
  const domain = email.substring(atIndex)
  
  // Show first 2 characters (or 1 if local part is short), mask the rest
  const visibleChars = Math.min(2, localPart.length)
  const masked = localPart.substring(0, visibleChars) + '***'
  
  return masked + domain
}

/**
 * Mask a phone number for logging
 * @param phone - Full phone number
 * @returns Masked phone like "***-***-1234"
 */
export function maskPhone(phone: string | undefined | null): string {
  if (!phone || typeof phone !== 'string') return '[no-phone]'
  
  // Keep only last 4 digits visible
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 4) return '[invalid-phone]'
  
  return '***-***-' + digits.slice(-4)
}
